/** Storyboard regenerate lifecycle — supersede old result, detach/remove
 * downstream canvas nodes, preserve assets (VISTOERA_STORYBOARD_REGENERATE_
 * RELATIONSHIP_RESET_CANVAS_CLEANUP_AND_GENERATING_STATE_P0_V1). Pure field
 * objects only; HTTP + graph mutations stay in `CanvasBoard.tsx`. */

import type { Node } from '@xyflow/react'
import type { StoryboardArtifact } from '../../../api'
import type { FlowNodeData } from './types'
import {
  applyGeneratedStoryboard,
  computeStoryboardParamsFingerprint,
  type StoryboardScriptContextField,
  type StoryboardSupportingAssetField,
} from './storyboardGenerationCanvas'
import { readProductionFormat } from './storyboardProductionFormat'
import {
  readShotGeneratorOwnership,
  readVideoProductionShotBindings,
  type FinalVideoArtifactSnapshot,
  type ShotBindingMap,
} from './videoProductionCanvas'

export type StoryboardRegenerateMode = 'keep_video_nodes' | 'remove_canvas_nodes'

export interface SupersededStoryboardSnapshot {
  storyboard: StoryboardArtifact
  version: number
  storyboardArtifactId: string | null
  shotBindings: ShotBindingMap
  finalVideoArtifact: FinalVideoArtifactSnapshot | null
  supersededAt: string
}

export interface StoryboardGenerationInputSnapshot {
  scriptContext: StoryboardScriptContextField | null
  supportingAssets: StoryboardSupportingAssetField[]
  shotCountMode: string
  shotCount: number | null
  durationMode: string
  totalDurationSec: number | null
  styleReference: string
  cameraLanguage: string
  productionFormat: ReturnType<typeof readProductionFormat>
  additionalInstructions: string
  paramsFingerprint: string
  capturedAt: string
}

export interface DetachedFromStoryboardMeta {
  shotId: string
  storyboardVersion: number
  storyboardArtifactId: string | null
  label: string
  detachedAt: string
}

const MAX_GENERATION_HISTORY = 20

const ACTIVE_REGENERATE_RUN_STATUSES = new Set(['SUBMITTING', 'QUEUED', 'GENERATING'])

export function isStoryboardRegenerateRunActive(fields: Record<string, unknown>): boolean {
  return Boolean(fields.storyboardRegenerateActive) && ACTIVE_REGENERATE_RUN_STATUSES.has(String(fields.runStatus || ''))
}

export function hasRecoverableSupersededStoryboard(fields: Record<string, unknown>): boolean {
  return Boolean(fields.supersededStoryboardSnapshot)
}

export function readSupersededStoryboardSnapshot(fields: Record<string, unknown>): SupersededStoryboardSnapshot | null {
  const raw = fields.supersededStoryboardSnapshot
  if (!raw || typeof raw !== 'object') return null
  const snap = raw as SupersededStoryboardSnapshot
  if (!snap.storyboard || typeof snap.version !== 'number') return null
  return snap
}

export function buildGenerationInputSnapshot(fields: Record<string, unknown>): StoryboardGenerationInputSnapshot {
  const supportingAssets = Array.isArray(fields.supportingAssets)
    ? (fields.supportingAssets as StoryboardSupportingAssetField[])
    : []
  return {
    scriptContext: (fields.scriptContext as StoryboardScriptContextField | null) ?? null,
    supportingAssets,
    shotCountMode: String(fields.shotCountMode ?? 'AUTO'),
    shotCount: typeof fields.shotCount === 'number' ? (fields.shotCount as number) : null,
    durationMode: String(fields.durationMode ?? 'AUTO'),
    totalDurationSec: typeof fields.totalDurationSec === 'number' ? (fields.totalDurationSec as number) : null,
    styleReference: String(fields.styleReference ?? 'AUTO'),
    cameraLanguage: String(fields.cameraLanguage ?? 'AUTO'),
    productionFormat: readProductionFormat(fields),
    additionalInstructions: String(fields.additionalInstructions ?? ''),
    paramsFingerprint: computeStoryboardParamsFingerprint(fields),
    capturedAt: new Date().toISOString(),
  }
}

/** Immediate orchestrator patch on regenerate confirm — hides old result from UI. */
export function beginStoryboardRegenerateOrchestratorPatch(fields: Record<string, unknown>): Record<string, unknown> | null {
  const storyboard = fields.storyboardResult as StoryboardArtifact | null
  const existingSuperseded = readSupersededStoryboardSnapshot(fields)

  if (!storyboard) {
    if (!existingSuperseded) return null
    return {
      runStatus: 'SUBMITTING',
      runErrorMessage: null,
      activeRunId: null,
      storyboardRegenerateActive: true,
      generationInputSnapshot:
        (fields.generationInputSnapshot as StoryboardGenerationInputSnapshot | null) ??
        buildGenerationInputSnapshot(fields),
    }
  }

  const snapshot: SupersededStoryboardSnapshot = {
    storyboard,
    version: (fields.storyboardResultVersion as number) || 1,
    storyboardArtifactId:
      (typeof fields.storyboardArtifactId === 'string' ? fields.storyboardArtifactId : null) ||
      storyboard.storyboard_artifact_id ||
      null,
    shotBindings: readVideoProductionShotBindings(fields),
    finalVideoArtifact: (fields.finalVideoArtifact as FinalVideoArtifactSnapshot | null) ?? null,
    supersededAt: new Date().toISOString(),
  }

  return {
    storyboardResult: null,
    storyboardArtifactId: null,
    shotBindings: {},
    finalVideoArtifact: null,
    assemblyRunStatus: null,
    assemblyErrorMessage: null,
    activeAssemblyId: null,
    finalVideoFormatMismatch: false,
    supersededStoryboardSnapshot: snapshot,
    storyboardRegenerateActive: true,
    generationInputSnapshot: buildGenerationInputSnapshot(fields),
    runStatus: 'SUBMITTING',
    runErrorMessage: null,
  }
}

export function restoreSupersededStoryboardFields(fields: Record<string, unknown>): Record<string, unknown> | null {
  const snap = readSupersededStoryboardSnapshot(fields)
  if (!snap) return null
  return {
    storyboardResult: snap.storyboard,
    storyboardResultVersion: snap.version,
    storyboardArtifactId: snap.storyboardArtifactId,
    shotBindings: snap.shotBindings,
    finalVideoArtifact: snap.finalVideoArtifact,
    supersededStoryboardSnapshot: null,
    storyboardRegenerateActive: false,
    generationInputSnapshot: null,
    runStatus: null,
    runErrorMessage: null,
    activeRunId: null,
  }
}

export function finalizeStoryboardRegenerateSuccess(
  fields: Record<string, unknown>,
  storyboard: StoryboardArtifact,
  runMeta: { runId: string; skillKey: string; skillVersion: string; providerOptionId: string | null },
): Record<string, unknown> {
  const superseded = readSupersededStoryboardSnapshot(fields)
  const applied = applyGeneratedStoryboard({ ...fields, storyboardResult: null }, storyboard, runMeta)
  if (applied.runStatus === 'ERROR') {
    return {
      ...applied,
      storyboardRegenerateActive: fields.storyboardRegenerateActive ?? false,
      supersededStoryboardSnapshot: fields.supersededStoryboardSnapshot ?? null,
      generationInputSnapshot: fields.generationInputSnapshot ?? null,
    }
  }

  const history = Array.isArray(applied.generationHistory) ? (applied.generationHistory as unknown[]) : []
  const nextHistory =
    superseded
      ? [
          {
            storyboard: superseded.storyboard,
            version: superseded.version,
            completedAt: superseded.supersededAt,
          },
          ...history,
        ].slice(0, MAX_GENERATION_HISTORY)
      : history

  return {
    ...applied,
    generationHistory: nextHistory,
    activeRunId: null,
    runStatus: null,
    runErrorMessage: null,
    shotBindings: {},
    finalVideoArtifact: null,
    assemblyRunStatus: null,
    assemblyErrorMessage: null,
    activeAssemblyId: null,
    supersededStoryboardSnapshot: null,
    storyboardRegenerateActive: false,
    generationInputSnapshot: null,
  }
}

export function collectStoryboardDerivedCanvasNodeIds(
  orchestratorNodeId: string,
  bindings: ShotBindingMap,
  nodes: Node[],
): string[] {
  const generatorIds = new Set<string>()
  for (const binding of Object.values(bindings)) {
    if (binding?.advancedVideoNodeId) generatorIds.add(binding.advancedVideoNodeId)
  }
  for (const n of nodes) {
    const ownership = readShotGeneratorOwnership(n)
    if (ownership?.boundOrchestratorNodeId === orchestratorNodeId) {
      generatorIds.add(n.id)
    }
  }
  const ids = new Set(generatorIds)
  for (const n of nodes) {
    const data = n.data as unknown as FlowNodeData
    if (data.nodeType !== 'generate_image') continue
    const f = (data.fields || {}) as Record<string, unknown>
    const candidate = f.candidateForVideoKeyframe as { videoNodeId?: string } | null | undefined
    const boundVideo = (f.boundVideoNodeId as string | undefined) || candidate?.videoNodeId
    const boundOrch = f.boundOrchestratorNodeId as string | undefined
    if (boundOrch === orchestratorNodeId || (boundVideo && generatorIds.has(boundVideo))) {
      ids.add(n.id)
    }
  }
  return [...ids]
}

export function buildDetachedVideoNodeFieldsPatch(
  videoFields: Record<string, unknown>,
  meta: {
    shotId: string
    storyboardVersion: number
    storyboardArtifactId: string | null
    shotIndex?: number | null
  },
): Record<string, unknown> {
  const shotLabel =
    meta.shotIndex != null && meta.shotIndex >= 0 ? `旧分镜 #${meta.shotIndex + 1}` : '旧分镜'
  const detached: DetachedFromStoryboardMeta = {
    shotId: meta.shotId,
    storyboardVersion: meta.storyboardVersion,
    storyboardArtifactId: meta.storyboardArtifactId,
    label: shotLabel,
    detachedAt: new Date().toISOString(),
  }
  const patch: Record<string, unknown> = {
    boundShotId: null,
    sourceShotId: null,
    boundOrchestratorNodeId: null,
    boundVideoProductionNodeId: null,
    boundStoryboardArtifactId: null,
    detachedFromStoryboard: detached,
  }
  if (videoFields.formatSource === 'storyboard') {
    patch.formatSource = null
  }
  return patch
}

/** Whether a generate_video node is still bound to a storyboard orchestrator. */
export function isVideoNodeStoryboardBound(fields: Record<string, unknown>): boolean {
  return Boolean(fields.boundOrchestratorNodeId || fields.boundVideoProductionNodeId)
}
